import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Card,
  DatePicker,
  Descriptions,
  Drawer,
  Form,
  Input,
  Modal,
  Radio,
  Space,
  Spin,
  Table,
  Tag,
  Tabs,
  Timeline,
  Typography,
  Empty,
  message,
} from 'antd';
import type { RadioChangeEvent } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  CheckCircleOutlined,
  EyeOutlined,
  HistoryOutlined,
  OrderedListOutlined,
  ReloadOutlined,
  SearchOutlined,
  WalletOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  getServiceProviderWithdrawReviewDetail,
  listServiceProviderWithdrawReviews,
  reviewServiceProviderWithdraw,
  type ServiceProviderWithdrawReviewItem,
  type ServiceProviderWithdrawStatus,
} from '../../services/serviceProviderWithdrawReviewService';
import { useHorizontalTableWheel } from '../../hooks/useHorizontalTableWheel';
import { serviceBookingOrderService, type ServiceBookingOrderSubItem } from '../../services/serviceBookingOrderService';
import {
  listServiceProviderSettlements,
  listServiceProviderWithdrawRecords,
  type ServiceProviderSettlementListItem,
  type ServiceProviderWithdrawRecordListItem,
} from '../../services/serviceProviderDetailFlowService';

const { RangePicker } = DatePicker;
const { Text } = Typography;

interface SearchFormValues {
  keyword?: string;
  status?: ServiceProviderWithdrawStatus | 'all';
  dateRange?: [dayjs.Dayjs, dayjs.Dayjs];
}

interface ReviewFormValues {
  action: 'approve' | 'reject';
  remark?: string;
}

type HistoryView = 'settlements' | 'withdrawals';
type SettlementStatus = 'all' | 'settled' | 'pending';

const statusOptions = [
  { label: '全部', value: 'all' },
  { label: '待审核', value: 'pending_review' },
  { label: '处理中', value: 'processing' },
  { label: '已到账', value: 'paid' },
  { label: '已驳回', value: 'rejected' },
  { label: '待收款', value: 'pending_receive' },
  { label: '失败/取消', value: 'failed' },
];

const statusMeta: Record<string, { color: string; text: string }> = {
  pending_review: { color: 'processing', text: '待审核' },
  processing: { color: 'processing', text: '处理中' },
  pending_receive: { color: 'warning', text: '待收款' },
  paid: { color: 'success', text: '已到账' },
  rejected: { color: 'error', text: '已驳回' },
  failed: { color: 'error', text: '提现失败' },
  canceled: { color: 'default', text: '已取消' },
};

const moneyFormatter = (value?: number) =>
  `￥${Number(value || 0).toLocaleString('zh-CN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatNotifyResponse = (value?: string) => {
  if (!value) {
    return '-';
  }
  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    return value;
  }
};

const renderStatus = (status: string, fallback?: string) => {
  const meta = statusMeta[status] || { color: 'default', text: fallback || status || '-' };
  return <Tag color={meta.color}>{meta.text}</Tag>;
};

const StaffWithdrawReview: React.FC = () => {
  const [searchForm] = Form.useForm<SearchFormValues>();
  const [reviewForm] = Form.useForm<ReviewFormValues>();
  const tableWrapRef = useHorizontalTableWheel();
  const reviewAction = Form.useWatch('action', reviewForm);

  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [items, setItems] = useState<ServiceProviderWithdrawReviewItem[]>([]);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 10, total: 0 });
  const [detailOpen, setDetailOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [currentDetail, setCurrentDetail] = useState<ServiceProviderWithdrawReviewItem | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyView, setHistoryView] = useState<HistoryView>('settlements');
  const [settlementStatus, setSettlementStatus] = useState<SettlementStatus>('settled');
  const [historyRecord, setHistoryRecord] = useState<ServiceProviderWithdrawReviewItem | null>(null);
  const [settlementItems, setSettlementItems] = useState<ServiceProviderSettlementListItem[]>([]);
  const [withdrawalItems, setWithdrawalItems] = useState<ServiceProviderWithdrawRecordListItem[]>([]);
  const [settlementPagination, setSettlementPagination] = useState({ page: 1, pageSize: 10, total: 0 });
  const [withdrawalPagination, setWithdrawalPagination] = useState({ page: 1, pageSize: 10, total: 0 });
  const [serviceFlowOpen, setServiceFlowOpen] = useState(false);
  const [serviceFlowLoading, setServiceFlowLoading] = useState(false);
  const [serviceFlowItem, setServiceFlowItem] = useState<ServiceBookingOrderSubItem | null>(null);

  const loadData = useCallback(
    async (page: number, pageSize: number) => {
      setLoading(true);
      try {
        const values = searchForm.getFieldsValue();
        const response = await listServiceProviderWithdrawReviews({
          page,
          pageSize,
          keyword: values.keyword || undefined,
          status: values.status || 'pending_review',
          startDate: values.dateRange?.[0]?.format('YYYY-MM-DD'),
          endDate: values.dateRange?.[1]?.format('YYYY-MM-DD'),
        });
        setItems(response.list || []);
        setPagination({
          page,
          pageSize,
          total: response.total || 0,
        });
      } catch {
        message.error('加载提现审核列表失败');
      } finally {
        setLoading(false);
      }
    },
    [searchForm],
  );

  useEffect(() => {
    void loadData(1, 10);
  }, [loadData]);

  const loadDetail = async (id: string, openDrawer = true) => {
    setDetailLoading(true);
    try {
      const detail = await getServiceProviderWithdrawReviewDetail(id);
      setCurrentDetail(detail);
      if (openDrawer) {
        setDetailOpen(true);
      }
      return detail;
    } catch {
      message.error('加载提现详情失败');
      return null;
    } finally {
      setDetailLoading(false);
    }
  };

  const handleSearch = () => {
    void loadData(1, pagination.pageSize);
  };

  const handleStatusChange = (event: RadioChangeEvent) => {
    searchForm.setFieldsValue({ status: event.target.value || 'all' });
    void loadData(1, pagination.pageSize);
  };

  const handleReset = () => {
    searchForm.setFieldsValue({
      keyword: undefined,
      status: 'pending_review',
      dateRange: undefined,
    });
    void loadData(1, pagination.pageSize);
  };

  const handleOpenReview = async (record: ServiceProviderWithdrawReviewItem) => {
    let detail = currentDetail;
    if (!detail || detail.id !== record.id) {
      detail = await loadDetail(record.id, false);
    }
    if (!detail) {
      return;
    }
    reviewForm.setFieldsValue({
      action: 'approve',
      remark: '',
    });
    setReviewOpen(true);
  };

  const handleSubmitReview = async () => {
    if (!currentDetail) {
      return;
    }
    try {
      const values = await reviewForm.validateFields();
      setReviewSubmitting(true);
      const detail = await reviewServiceProviderWithdraw(currentDetail.id, {
        action: values.action,
        remark: values.remark?.trim() || '',
      });
      setCurrentDetail(detail);
      setReviewOpen(false);
      message.success(values.action === 'approve' ? '提现审核通过成功' : '提现驳回成功');
      void loadData(pagination.page, pagination.pageSize);
    } catch (error: any) {
      if (error?.errorFields) {
        return;
      }
      message.error(error?.response?.data?.message || '提交提现审核失败');
    } finally {
      setReviewSubmitting(false);
    }
  };

  const loadSettlementHistory = async (
    record: ServiceProviderWithdrawReviewItem,
    status: SettlementStatus,
    page: number,
    pageSize: number,
  ) => {
    setHistoryLoading(true);
    try {
      const response = await listServiceProviderSettlements(record.providerId, {
        page,
        pageSize,
        ...(status === 'all' ? {} : { status }),
      });
      setSettlementItems(response.list || []);
      setSettlementPagination({ page, pageSize, total: response.total || 0 });
    } catch {
      message.error('加载订单结算记录失败');
    } finally {
      setHistoryLoading(false);
    }
  };

  const loadWithdrawalHistory = async (
    record: ServiceProviderWithdrawReviewItem,
    page: number,
    pageSize: number,
  ) => {
    setHistoryLoading(true);
    try {
      const response = await listServiceProviderWithdrawRecords(record.providerId, {
        page,
        pageSize,
      });
      setWithdrawalItems(response.list || []);
      setWithdrawalPagination({ page, pageSize, total: response.total || 0 });
    } catch {
      message.error('加载申请流水失败');
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleOpenHistory = (record: ServiceProviderWithdrawReviewItem) => {
    setHistoryRecord(record);
    setHistoryView('settlements');
    setSettlementStatus('settled');
    setHistoryOpen(true);
    void loadSettlementHistory(record, 'settled', 1, 10);
  };

  const handleHistoryViewChange = (nextView: string) => {
    const selectedView = nextView as HistoryView;
    setHistoryView(selectedView);
    if (!historyRecord) {
      return;
    }
    if (selectedView === 'settlements') {
      void loadSettlementHistory(historyRecord, settlementStatus, 1, settlementPagination.pageSize);
    } else {
      void loadWithdrawalHistory(historyRecord, 1, withdrawalPagination.pageSize);
    }
  };

  const handleSettlementStatusChange = (event: RadioChangeEvent) => {
    const nextStatus = event.target.value as SettlementStatus;
    setSettlementStatus(nextStatus);
    if (historyRecord) {
      void loadSettlementHistory(historyRecord, nextStatus, 1, settlementPagination.pageSize);
    }
  };

  const handleOpenServiceFlow = async (record: ServiceProviderSettlementListItem) => {
    setServiceFlowOpen(true);
    setServiceFlowLoading(true);
    setServiceFlowItem(null);
    try {
      const order = await serviceBookingOrderService.get(record.orderId);
      const item = (order.items || []).find(
        subOrder => subOrder.id === record.subOrderId || subOrder.orderNo === record.subOrderNo,
      );
      if (!item) {
        message.warning('未找到该订单的服务流程');
        return;
      }
      setServiceFlowItem(item);
    } catch {
      message.error('加载服务流程失败');
    } finally {
      setServiceFlowLoading(false);
    }
  };
  const settlementColumns: ColumnsType<ServiceProviderSettlementListItem> = [
    {
      title: '订单号',
      width: 190,
      render: (_, record) => (
        <div>
          <div>{record.orderNo || '-'}</div>
          <Text type="secondary">{record.subOrderNo || '-'}</Text>
        </div>
      ),
    },
    {
      title: '客户',
      width: 150,
      render: (_, record) => (
        <div>
          <div>{record.contactName || '-'}</div>
          <Text type="secondary">{record.contactPhone || '-'}</Text>
        </div>
      ),
    },
    {
      title: '服务内容',
      dataIndex: 'serviceContent',
      width: 180,
      ellipsis: true,
      render: (value: string) => value || '-',
    },
    {
      title: '结算金额',
      dataIndex: 'settlementAmount',
      width: 120,
      render: (value: number) => <Text strong>{moneyFormatter(value)}</Text>,
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 100,
      render: (value: string) => (
        <Tag color={value === 'settled' ? 'success' : 'warning'}>
          {value === 'settled' ? '已结算' : '未结算'}
        </Tag>
      ),
    },
    {
      title: '服务日期',
      dataIndex: 'serviceDate',
      width: 120,
      render: (value: string) => value || '-',
    },
    {
      title: '完成时间',
      dataIndex: 'completedAt',
      width: 170,
      render: (value: string) => value || '-',
    },
    {
      title: '结算时间',
      dataIndex: 'settledAt',
      width: 170,
      render: (value: string) => value || '-',
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        <Button type="link" size="small" icon={<OrderedListOutlined />} onClick={() => void handleOpenServiceFlow(record)}>
          服务流程
        </Button>
      ),
    },
  ];

  const withdrawalColumns: ColumnsType<ServiceProviderWithdrawRecordListItem> = [
    {
      title: '提现金额',
      dataIndex: 'amount',
      width: 120,
      render: (value: number) => <Text strong type="danger">{moneyFormatter(value)}</Text>,
    },
    {
      title: '提现方式',
      dataIndex: 'methodText',
      width: 120,
      render: (value: string) => value || '-',
    },
    {
      title: '收款信息',
      width: 190,
      render: (_, record) => (
        <div>
          <div>{record.accountName || '-'}</div>
          <Text type="secondary">{record.accountNo || '-'}</Text>
        </div>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 110,
      render: (value: string) => renderStatus(value),
    },
    {
      title: '申请时间',
      dataIndex: 'requestedAt',
      width: 170,
      render: (value: string) => value || '-',
    },
    {
      title: '审核时间',
      dataIndex: 'reviewedAt',
      width: 170,
      render: (value: string) => value || '-',
    },
    {
      title: '到账时间',
      dataIndex: 'arrivedAt',
      width: 170,
      render: (value: string) => value || '-',
    },
    {
      title: '状态说明',
      dataIndex: 'statusRemark',
      width: 220,
      ellipsis: true,
      render: (value: string) => value || '-',
    },
  ];

  const columns: ColumnsType<ServiceProviderWithdrawReviewItem> = [
    {
      title: '服务人员',
      dataIndex: 'providerName',
      width: 180,
      render: (_, record) => (
        <div>
          <div>{record.providerName || '-'}</div>
          <Text type="secondary">{record.providerPhone || '-'}</Text>
        </div>
      ),
    },
    {
      title: '提现金额',
      dataIndex: 'amount',
      width: 130,
      render: (amount: number) => <Text strong type="danger">{moneyFormatter(amount)}</Text>,
    },
    {
      title: '提现方式',
      dataIndex: 'methodText',
      width: 120,
      render: (value: string) => value || '-',
    },
    {
      title: '收款信息',
      dataIndex: 'accountName',
      width: 220,
      render: (_, record) => (
        <div>
          <div>{record.accountName || '-'}</div>
          <Text type="secondary">{record.accountNo || '-'}</Text>
        </div>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 110,
      render: (status: string, record) => renderStatus(status, record.statusText),
    },
    {
      title: '申请时间',
      dataIndex: 'requestedAt',
      width: 170,
      render: (value: string) => value || '-',
    },
    {
      title: '审核时间',
      dataIndex: 'reviewedAt',
      width: 170,
      render: (value: string) => value || '-',
    },
    {
      title: '备注',
      dataIndex: 'statusRemark',
      ellipsis: true,
      render: (value: string) => value || '-',
    },
    {
      title: '操作',
      key: 'action',
      width: 270,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Button size="small" icon={<EyeOutlined />} onClick={() => void loadDetail(record.id)}>
            详情
          </Button>
          <Button size="small" icon={<HistoryOutlined />} onClick={() => handleOpenHistory(record)}>
            申请历史
          </Button>
          {record.status === 'pending_review' && (
            <Button size="small" type="primary" icon={<CheckCircleOutlined />} onClick={() => void handleOpenReview(record)}>
              审核
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card
        title={(
          <Space>
            <WalletOutlined />
            <span>提现审核</span>
          </Space>
        )}
        extra={(
          <Button icon={<ReloadOutlined />} onClick={() => void loadData(pagination.page, pagination.pageSize)}>
            刷新
          </Button>
        )}
      >
        <Form
          form={searchForm}
          layout="inline"
          initialValues={{ status: 'pending_review' }}
          style={{ marginBottom: 16, rowGap: 12 }}
        >
          <Form.Item name="keyword" label="关键词">
            <Input allowClear placeholder="姓名 / 手机号 / 收款人 / 收款账号" style={{ width: 260 }} />
          </Form.Item>
          <Form.Item name="status" label="状态">
            <Radio.Group
              optionType="button"
              buttonStyle="solid"
              options={statusOptions}
              onChange={handleStatusChange}
            />
          </Form.Item>
          <Form.Item name="dateRange" label="申请时间">
            <RangePicker allowClear />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
                查询
              </Button>
              <Button onClick={handleReset}>重置</Button>
            </Space>
          </Form.Item>
        </Form>

        <div ref={tableWrapRef}>
          <Table
            rowKey="id"
            loading={loading}
            columns={columns}
            dataSource={items}
            scroll={{ x: 1350 }}
            pagination={{
              current: pagination.page,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              showTotal: total => `共 ${total} 条`,
              onChange: (page, pageSize) => void loadData(page, pageSize),
            }}
          />
        </div>
      </Card>

      <Drawer title="提现详情" open={detailOpen} onClose={() => setDetailOpen(false)} size={720}>
        <Card loading={detailLoading} size="small">
          {currentDetail && (
            <Descriptions column={2} size="small">
              <Descriptions.Item label="服务人员">{currentDetail.providerName || '-'}</Descriptions.Item>
              <Descriptions.Item label="手机号">{currentDetail.providerPhone || '-'}</Descriptions.Item>
              <Descriptions.Item label="提现金额">{moneyFormatter(currentDetail.amount)}</Descriptions.Item>
              <Descriptions.Item label="提现方式">{currentDetail.methodText || '-'}</Descriptions.Item>
              <Descriptions.Item label="收款姓名">{currentDetail.accountName || '-'}</Descriptions.Item>
              <Descriptions.Item label="收款账号">{currentDetail.accountNo || '-'}</Descriptions.Item>
              <Descriptions.Item label="状态">
                {renderStatus(currentDetail.status, currentDetail.statusText)}
              </Descriptions.Item>
              <Descriptions.Item label="审核模式">{currentDetail.auditMode || '-'}</Descriptions.Item>
              <Descriptions.Item label="提现前余额">{moneyFormatter(currentDetail.beforeBalance)}</Descriptions.Item>
              <Descriptions.Item label="处理后余额">{moneyFormatter(currentDetail.afterBalance)}</Descriptions.Item>
              <Descriptions.Item label="当前可提现余额">{moneyFormatter(currentDetail.withdrawableNow)}</Descriptions.Item>
              <Descriptions.Item label="申请时间">{currentDetail.requestedAt || '-'}</Descriptions.Item>
              <Descriptions.Item label="审核时间">{currentDetail.reviewedAt || '-'}</Descriptions.Item>
              <Descriptions.Item label="到账时间">{currentDetail.arrivedAt || '-'}</Descriptions.Item>
              <Descriptions.Item label="状态说明" span={2}>
                {currentDetail.statusRemark || '-'}
              </Descriptions.Item>
            </Descriptions>
          )}
        </Card>

        <Card title="微信提现回调日志" size="small" style={{ marginTop: 16 }}>
          {currentDetail?.wechatNotifyLogs?.length ? (
            <Space orientation="vertical" size={12} style={{ width: '100%' }}>
              {currentDetail.wechatNotifyLogs.map((item, index) => (
                <Card
                  key={item.id || index}
                  size="small"
                  title={`回调 ${index + 1}`}
                  extra={<Tag color={item.status === 'success' ? 'success' : 'error'}>{item.status || '-'}</Tag>}
                >
                  <Descriptions column={1} size="small">
                    <Descriptions.Item label="回调类型">{item.notifyType || '-'}</Descriptions.Item>
                    <Descriptions.Item label="回调时间">{item.createdAt || '-'}</Descriptions.Item>
                    <Descriptions.Item label="错误信息">{item.errorMsg || '-'}</Descriptions.Item>
                    <Descriptions.Item label="解密数据">
                      <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                        {formatNotifyResponse(item.responseData)}
                      </pre>
                    </Descriptions.Item>
                  </Descriptions>
                </Card>
              ))}
            </Space>
          ) : (
            <Empty description="暂无微信提现回调日志" />
          )}
        </Card>

        {currentDetail?.status === 'pending_review' && (
          <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
            <Button type="primary" icon={<CheckCircleOutlined />} onClick={() => void handleOpenReview(currentDetail)}>
              提交审核
            </Button>
          </div>
        )}
      </Drawer>

      <Modal
        title={`服务流程${serviceFlowItem?.serviceDate ? ` - ${serviceFlowItem.serviceDate}` : ''}`}
        open={serviceFlowOpen}
        onCancel={() => {
          setServiceFlowOpen(false);
          setServiceFlowItem(null);
        }}
        footer={null}
        width={860}
        destroyOnHidden
      >
        {serviceFlowLoading ? (
          <div style={{ minHeight: 180, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Spin />
          </div>
        ) : !serviceFlowItem ? (
          <Empty description="暂无服务流程记录" />
        ) : (
          <Space orientation="vertical" size={16} style={{ width: '100%' }}>
            <Descriptions column={2} size="small" bordered>
              <Descriptions.Item label="服务日期">{serviceFlowItem.serviceDate || '-'}</Descriptions.Item>
              <Descriptions.Item label="服务时段">{serviceFlowItem.serviceTimeDesc || '-'}</Descriptions.Item>
              <Descriptions.Item label="服务人员">{serviceFlowItem.assignedStaffName || '-'}</Descriptions.Item>
              <Descriptions.Item label="联系电话">{serviceFlowItem.assignedStaffPhone || '-'}</Descriptions.Item>
              <Descriptions.Item label="服务状态">{serviceFlowItem.statusText || serviceFlowItem.status || '-'}</Descriptions.Item>
              <Descriptions.Item label="完成时间">{serviceFlowItem.completedAt || '-'}</Descriptions.Item>
            </Descriptions>
            <Card size="small" title="签到信息和服务中流程">
              {(serviceFlowItem.serviceRecords || []).length ? (
                <Timeline
                  items={serviceFlowItem.serviceRecords.map((record, index) => ({
                    key: `${record.occurredAt || index}-${record.title || record.processStepName || index}`,
                    color: record.title?.includes('完成') ? 'green' : record.title?.includes('签到') ? 'blue' : 'gray',
                    children: (
                      <div>
                        <div style={{ fontWeight: 600 }}>
                          {record.processStepName || record.title || `流程记录${index + 1}`}
                        </div>
                        <div style={{ color: '#666', marginTop: 4 }}>
                          {record.occurredAt || '-'} {record.operator ? `｜${record.operator}` : ''}
                        </div>
                        {record.desc ? <div style={{ marginTop: 6 }}>{record.desc}</div> : null}
                        {record.remark ? <div style={{ marginTop: 6 }}>备注：{record.remark}</div> : null}
                      </div>
                    ),
                  }))}
                />
              ) : (
                <Empty description="暂无签到或服务流程记录" />
              )}
            </Card>
          </Space>
        )}
      </Modal>
      <Modal
        title={historyRecord?.providerName ? '申请历史 - ' + historyRecord.providerName : '申请历史'}
        open={historyOpen}
        onCancel={() => setHistoryOpen(false)}
        footer={null}
        width={1180}
        destroyOnHidden
      >
        <Space orientation="vertical" size={16} style={{ width: '100%' }}>
          <Tabs
            activeKey={historyView}
            onChange={handleHistoryViewChange}
            items={[
              {
                key: 'settlements',
                label: (
                  <Space size={6}>
                    <WalletOutlined />
                    <span>订单结算记录</span>
                  </Space>
                ),
              },
              {
                key: 'withdrawals',
                label: (
                  <Space size={6}>
                    <HistoryOutlined />
                    <span>申请流水</span>
                  </Space>
                ),
              },
            ]}
            style={{ marginBottom: 0 }}
          />

          {historyView === 'settlements' ? (
            <>
              <Radio.Group
                optionType="button"
                buttonStyle="solid"
                value={settlementStatus}
                onChange={handleSettlementStatusChange}
                options={[
                  { label: '全部', value: 'all' },
                  { label: '已结算', value: 'settled' },
                  { label: '未结算', value: 'pending' },
                ]}
              />
              <Table
                rowKey="id"
                loading={historyLoading}
                columns={settlementColumns}
                dataSource={settlementItems}
                scroll={{ x: 1200 }}
                pagination={{
                  current: settlementPagination.page,
                  pageSize: settlementPagination.pageSize,
                  total: settlementPagination.total,
                  showSizeChanger: true,
                  showTotal: total => '共 ' + total + ' 条',
                  onChange: (page, pageSize) => {
                    if (historyRecord) {
                      void loadSettlementHistory(historyRecord, settlementStatus, page, pageSize);
                    }
                  },
                }}
              />
            </>
          ) : (
            <Table
              rowKey="id"
              loading={historyLoading}
              columns={withdrawalColumns}
              dataSource={withdrawalItems}
              scroll={{ x: 1250 }}
              pagination={{
                current: withdrawalPagination.page,
                pageSize: withdrawalPagination.pageSize,
                total: withdrawalPagination.total,
                showSizeChanger: true,
                showTotal: total => '共 ' + total + ' 条',
                onChange: (page, pageSize) => {
                  if (historyRecord) {
                    void loadWithdrawalHistory(historyRecord, page, pageSize);
                  }
                },
              }}
            />
          )}
        </Space>
      </Modal>

      <Modal
        title="提现审核"
        open={reviewOpen}
        onCancel={() => setReviewOpen(false)}
        onOk={handleSubmitReview}
        confirmLoading={reviewSubmitting}
        okText="提交审核"
        cancelText="取消"
        width={640}
        destroyOnHidden
      >
        <Space orientation="vertical" size={16} style={{ width: '100%' }}>
          <Card size="small" title="提现信息">
            <Descriptions column={2} size="small">
              <Descriptions.Item label="服务人员">{currentDetail?.providerName || '-'}</Descriptions.Item>
              <Descriptions.Item label="手机号">{currentDetail?.providerPhone || '-'}</Descriptions.Item>
              <Descriptions.Item label="提现金额">{moneyFormatter(currentDetail?.amount)}</Descriptions.Item>
              <Descriptions.Item label="提现方式">{currentDetail?.methodText || '-'}</Descriptions.Item>
              <Descriptions.Item label="收款姓名">{currentDetail?.accountName || '-'}</Descriptions.Item>
              <Descriptions.Item label="收款账号">{currentDetail?.accountNo || '-'}</Descriptions.Item>
            </Descriptions>
          </Card>

          <Form form={reviewForm} layout="vertical" initialValues={{ action: 'approve', remark: '' }}>
            <Form.Item name="action" label="审核结果" rules={[{ required: true, message: '请选择审核结果' }]}>
              <Radio.Group>
                <Radio value="approve">通过并发起提现</Radio>
                <Radio value="reject">驳回并退回余额</Radio>
              </Radio.Group>
            </Form.Item>
            <Form.Item
              name="remark"
              label="审核备注"
              extra={reviewAction === 'reject' ? '驳回时必须填写原因，服务人员会看到该备注。' : '通过时可选填备注。'}
              rules={[
                {
                  validator: async (_, value) => {
                    if (reviewAction === 'reject' && !String(value || '').trim()) {
                      throw new Error('驳回时请填写审核备注');
                    }
                  },
                },
              ]}
            >
              <Input.TextArea rows={4} maxLength={255} showCount placeholder="请输入审核备注" />
            </Form.Item>
          </Form>
        </Space>
      </Modal>
    </div>
  );
};

export default StaffWithdrawReview;
